// Payouts table — live data from usePayouts(), phase 5.
//
// Mock data source removed (D-03, ROADMAP criterion 4). The screen reads from
// a real usePayouts() TanStack Query hook with 30s background polling.
//
// Amount display: amount_minor is bigint (INR paise) after deserialization in
// listPayouts(). Passed directly to fmtCcyMinor(p.amount_minor) — no extra
// BigInt() wrapper needed (WR-03 fix).
// Float conversion is forbidden — it causes a 100× INR display bug (INV-money-bigint-minor).
//
// Status enum: pending / success / failure (live backend values).
// The old mock values (completed/processing/scheduled/review/failed) are gone.
import { useState, useEffect, useMemo, lazy, Suspense, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, Segmented, StatusPill } from "@/components/UI";
import { FailureReason } from "@/components/FailureReason";
import {
  fmtCcyMinor,
  fmtMoney,
  formatDateTimeFull,
  formatDaySpan,
} from "@/utils/format";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { downloadCsv } from "@/utils/csv";
import { apiErrorMessage } from "@/utils/apiError";
import { dateRangeBounds, isReversedRange } from "@/utils/dateRange";
import { isAmountInPayoutBand } from "@/utils/payoutLimits";
import {
  countSingletonBatches,
  groupByBatch,
  statusCounts,
  type GroupSort,
  type PayoutGroup,
} from "@/utils/payoutGroups";
import { usePayouts } from "@/queries/usePayouts";
import { useBankAccounts } from "@/queries/useBankAccounts";
import { useBeneficiaries } from "@/queries/useBeneficiaries";
import { useUI } from "@/store/ui";
import { useAuth, isAdminRole } from "@/store/auth";
import { providerLabel } from "@/utils/providerLabel";
import {
  createPayout,
  forceRepollPayout,
  listAllPayouts,
  type Payout,
  type PayoutListParams,
} from "@/api/payouts";

// Lazy-load the UploadBatchModal — it transitively pulls in xlsx (~600 kB
// minified) and papaparse, which only matter when the user actually opens
// the CSV upload wizard. Code-split here keeps the main bundle tight.
const UploadBatchModal = lazy(() =>
  import("@/components/UploadBatchModal").then((m) => ({
    default: m.UploadBatchModal,
  })),
);

type LivePayoutStatus = Payout["status"];

// AUDIT-PASS-10 FE-L15 follow-up: providerDisplayName removed — call sites
// now import providerLabel from @/utils/providerLabel directly. The label
// output changes from "IDFC" → "IDFC (IDFC)" as part of the
// unification with the platform-side pages, matching the more-accurate
// rail identifier.

// rupeesToPaise parses a whole-rupees string ("1500") into exact paise as a
// bigint. String-based — never parseFloat (CLAUDE.md rule 6). Returns null
// for malformed or non-positive input.
//
// Fractional paise are rejected: IMPS/NEFT both settle in whole rupees and
// IDFC's sandbox rejects "1500.00"-style amounts with a misleading
// bounds error (see payouts-backend 2747b54 — ErrAmountNotWholeRupees +
// integer formatter). Keep the panel's pre-validation tight so the request
// never leaves the browser when a decimal slips in via paste.
// Staleness threshold — pending rows older than this are surfaced with
// an inline "stale Nh" badge so the operator can spot upstream-stuck
// payouts in the table. Mirrors the backend's /v1/stats stale_pending
// threshold (currently hardcoded 24h on both sides). If the backend
// adds an env var to override, mirror here.
const STALE_PENDING_HOURS = 24;

function ageInHours(createdAt: string): number {
  const created = new Date(createdAt).getTime();
  const now = Date.now();
  // Math.max guards against clock skew: a fresh row whose timestamp is
  // a few ms in the future shouldn't surface as "−1h".
  return Math.max(0, Math.floor((now - created) / (3600 * 1000)));
}

function isStalePending(createdAt: string): boolean {
  return ageInHours(createdAt) >= STALE_PENDING_HOURS;
}

function rupeesToPaise(input: string): bigint | null {
  const t = input.trim();
  if (!/^\d{1,12}$/.test(t)) return null;
  const paise = BigInt(t) * 100n;
  return paise > 0n ? paise : null;
}

// ─── New payout modal ─────────────────────────────────────────────────────────

function NewPayoutModal({
  open,
  onClose,
  merchantId,
}: {
  open: boolean;
  onClose: () => void;
  merchantId: number | null;
}) {
  const qc = useQueryClient();
  const { data: rails = [] } = useBankAccounts();
  const { data: beneficiaries = [] } = useBeneficiaries();
  // Hide provider name from merchant users — they see rails by their
  // own label only. Admin keeps the disambiguation suffix so two rails
  // with similar operator-supplied labels remain distinguishable.
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));

  const [bankAccountId, setBankAccountId] = useState("");
  const [beneficiaryId, setBeneficiaryId] = useState("");
  const [amount, setAmount] = useState("");
  const [txnMode, setTxnMode] = useState("1");
  const [requestId, setRequestId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Payout | null>(null);

  // Fresh idempotency key + cleared fields each time the modal opens.
  useEffect(() => {
    if (open) {
      setBankAccountId("");
      setBeneficiaryId("");
      setAmount("");
      setTxnMode("1");
      setRequestId(crypto.randomUUID());
      setSubmitting(false);
      setError(null);
      setDone(null);
    }
  }, [open]);

  const paise = rupeesToPaise(amount);
  // Band check is separate from "is it parseable" so we can surface a
  // specific inline error ("₹300 minimum…") instead of the generic
  // "enter a positive whole-rupee amount" copy. Both gate canSubmit so
  // the button stays disabled until the operator types a valid value.
  const inBand = paise !== null && isAmountInPayoutBand(paise);
  const canSubmit =
    !submitting &&
    bankAccountId !== "" &&
    beneficiaryId !== "" &&
    paise !== null &&
    inBand;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (merchantId == null) return; // the form is not rendered without a merchant
    if (paise === null) {
      setError("Enter a positive whole-rupee amount (e.g. 1500). Decimals are not allowed.");
      return;
    }
    if (!inBand) {
      // Mirrors backend ErrAmountOutOfRange. Operator gets the rule
      // before submit instead of reserving a row that bounces from
      // IDFC with code 102 after admin approval.
      setError("Amount must be between ₹300 and ₹1,00,000 per payout (per-transaction band).");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payout = await createPayout(merchantId, {
        bank_account_id: Number(bankAccountId),
        beneficiary_id: Number(beneficiaryId),
        // paise is bigint and flows through apiClient's json-bigint
        // transformRequest unchanged — never Number() it (CLAUDE.md rule 6).
        amount_minor: paise,
        txn_mode: Number(txnMode),
        request_id: requestId,
      });
      await qc.invalidateQueries({ queryKey: ["payouts", merchantId] });
      setDone(payout);
      // CTL bc0bcaa #M-1: reset submitting on success too, otherwise
      // busy={submitting} traps Escape/scrim/X on the success banner.
      // The footer switches to a "Close" button via the `done` branch but
      // keyboard-only users still expect Escape to dismiss.
      setSubmitting(false);
    } catch (err) {
      // Centralised mapping: known code → curated copy, else HTTP-status
      // fallback (404/409/422/429/5xx have specific copy), else the
      // generic message below. Code is appended in parens for support
      // tickets when status-fallback fires (apiErrorMessage handles that).
      setError(apiErrorMessage(err, "Could not initiate the payout. Try again."));
      setSubmitting(false);
    }
  }

  const blocked = rails.length === 0 || beneficiaries.length === 0;
  // Admins have no merchant memberships; until one is picked in the merchant
  // switcher (or none exist yet) a payout has no merchant to be scoped to.
  const noMerchant = merchantId == null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={submitting}
      title="New payout"
      foot={
        done || noMerchant ? (
          <button className="btn" type="button" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button
              className="btn ghost"
              type="button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              className="btn accent"
              type="submit"
              form="new-payout-form"
              disabled={!canSubmit || blocked}
            >
              {submitting ? "Initiating…" : "Initiate payout"}
            </button>
          </>
        )
      }
    >
      {noMerchant ? (
        <div className="dim" style={{ fontSize: 13 }}>
          Choose a merchant from the switcher at the top of the sidebar before
          initiating a payout — every payout is scoped to one merchant. If the
          switcher is empty, no merchants exist yet.
        </div>
      ) : done ? (
        <div className="stack-16">
          <div
            className="row gap-8"
            style={{ color: "var(--pos)", fontSize: 13 }}
          >
            <I.Check size={14} stroke={2} /> Payout #{done.id} initiated
          </div>
          <div className="dim" style={{ fontSize: 12 }}>
            {fmtCcyMinor(done.amount_minor, done.currency)} · status{" "}
            {done.status}. The table polls every 30s for status updates.
          </div>
        </div>
      ) : blocked ? (
        <div className="dim" style={{ fontSize: 13 }}>
          This merchant needs at least one bank rail and one beneficiary
          before a payout can be initiated.
        </div>
      ) : (
        <form id="new-payout-form" onSubmit={handleSubmit} className="stack-16">
          <div className="field">
            <label className="label" htmlFor="np-rail">
              Bank rail
            </label>
            {/* AUDIT-PASS-5 UI #25 — autoFocus moves from the amount field
                to the bank rail. The previous placement skipped the first
                two REQUIRED selects and landed on the third field, breaking
                tab-order continuity for keyboard users. Bank rail is the
                first field on the form, so this matches the natural
                top-to-bottom flow. */}
            <select
              id="np-rail"
              className="input"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              autoFocus
            >
              <option value="">Select a rail…</option>
              {rails.map((r) => (
                // AUDIT-PASS-9: include the provider in the option label so
                // ADMIN operators can deliberately pick IDFC vs Buckzy
                // when two rails carry similar labels. Merchant users see
                // the label only — the upstream provider identity is not
                // part of their model and showing it leaks operational
                // topology that doesn't help them pick the right rail.
                <option key={r.id} value={r.id}>
                  {r.label}
                  {isAdmin ? ` · ${providerLabel(r.provider)}` : ""} (ba_{r.id})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="np-bene">
              Beneficiary
            </label>
            <select
              id="np-bene"
              className="input"
              value={beneficiaryId}
              onChange={(e) => setBeneficiaryId(e.target.value)}
            >
              <option value="">Select a beneficiary…</option>
              {beneficiaries.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label} · {b.ifsc}
                  {b.is_registered ? "" : " (not registered)"}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="np-amount">
              Amount (INR)
            </label>
            <input
              id="np-amount"
              className="input"
              type="text"
              inputMode="numeric"
              placeholder="1500"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <div
              style={{ fontSize: 11, marginTop: 4, color: "var(--ink-3)" }}
            >
              Whole rupees only — IMPS/NEFT don't settle paise. Per-payout band: ₹300 to ₹1,00,000.
            </div>
            {amount.trim() !== "" && paise === null && (
              <div
                style={{
                  fontSize: 11,
                  marginTop: 4,
                  color: "var(--warn, #b42318)",
                }}
              >
                Enter a positive whole-rupee amount (no decimals).
              </div>
            )}
            {paise !== null && !inBand && (
              <div
                style={{
                  fontSize: 11,
                  marginTop: 4,
                  color: "var(--warn, #b42318)",
                }}
              >
                Amount must be between ₹300 and ₹1,00,000 per payout.
              </div>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor="np-mode">
              Transfer mode
            </label>
            <select
              id="np-mode"
              className="input"
              value={txnMode}
              onChange={(e) => setTxnMode(e.target.value)}
            >
              <option value="1">IMPS</option>
              <option value="2">NEFT</option>
            </select>
          </div>
          <div className="field">
            <label className="label">Idempotency key</label>
            <div
              className="input mono"
              style={{
                color: "var(--ink-3)",
                userSelect: "all",
                fontSize: 11,
              }}
              aria-readonly="true"
            >
              {requestId}
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
              Auto-generated — re-submitting with the same key returns the
              original payout instead of creating a duplicate.
            </div>
          </div>
          {error && (
            <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
              {error}
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}

const FILTERS: { value: LivePayoutStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "needs_approval", label: "Awaiting approval" },
  { value: "pending", label: "Pending" },
  { value: "success", label: "Success" },
  { value: "failure", label: "Failure" },
];

// Rows per page. The totals in the header never depend on it — they come
// from the server over every matching row.
const PAGE_SIZE = 100;

export function Payouts() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<LivePayoutStatus | "all">("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [minAmount, setMinAmount] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Batch ordering. /payouts is a HISTORY, not a work queue, so it opens on
  // the recent end — the opposite default to /approvals, where the oldest
  // unactioned batch is the one that needs a human. groupByBatch keeps its
  // "oldest" default so that page is unaffected.
  const [sortDir, setSortDir] = useState<GroupSort>("newest");
  // Fold one-row batches into a single "Single payouts" group. On by default:
  // a batch of one is a batch only in the database's opinion, and a history
  // page where every payment is its own collapsed card is a page you cannot
  // read. Operators who want the raw batch identity back untick it.
  const [groupSingles, setGroupSingles] = useState(true);
  const merchantId = useUI((s) => s.currentMerchantId);
  const addToast = useUI((s) => s.addToast);
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  // Search hits the server, so wait for typing to pause.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const minPaise = rupeesToPaise(minAmount);
  // Every filter is applied by the server, in SQL, over ALL of the
  // merchant's payouts. This page used to fetch the newest 500 rows and
  // filter and sum them here, so its totals silently left out everything
  // older — and disagreed with the dashboard, which counts in SQL.
  //
  // A reversed range is sent unbounded rather than as a 400; the
  // DateRangeFilter already flags it in place.
  const listParams: Omit<PayoutListParams, "limit" | "offset"> = useMemo(() => {
    const bounds = isReversedRange(dateFrom, dateTo) ? {} : dateRangeBounds(dateFrom, dateTo);
    return {
      status: filter === "all" ? undefined : filter,
      from: bounds.from,
      to: bounds.to,
      q: debouncedSearch || undefined,
      txn_mode: modeFilter === "all" ? undefined : Number(modeFilter),
      min_amount_minor: minPaise ?? undefined,
      sort: sortDir,
    };
  }, [filter, dateFrom, dateTo, debouncedSearch, modeFilter, minPaise, sortDir]);
  // Any filter change starts again from the first page.
  useEffect(() => setPage(0), [listParams]);

  const { data, isLoading, isFetching, isError, refetch } = usePayouts({
    ...listParams,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const payouts = data?.payouts ?? [];
  const total = data?.total ?? 0;
  const pageStart = page * PAGE_SIZE;
  const pageEnd = pageStart + payouts.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Status tabs carry the server's count for each — over the other filters,
  // so "Success · 588" is exactly what clicking it lists.
  const statusTabs = FILTERS.map((f) => {
    const counts = data?.statusCounts ?? {};
    const n =
      f.value === "all"
        ? Object.values(counts).reduce((a, b) => a + b, 0)
        : (counts[f.value] ?? 0);
    return { ...f, label: data ? `${f.label} · ${n.toLocaleString("en-IN")}` : f.label };
  });
  const [exporting, setExporting] = useState(false);

  // Track which row is currently being repolled so the affected button can
  // show a spinning state without disabling the rest of the column. Use a
  // Set so multiple terminal rows could in principle be repolled in parallel
  // — though typical operator workflow is one at a time.
  const [repolling, setRepolling] = useState<ReadonlySet<number>>(new Set());

  // Foldable batch state. Mirrors /approvals: every batch group renders
  // collapsed by default (just header + status distribution); click to
  // expand the row table. Keeps the page scannable when a merchant has
  // many CSV batches in their history.
  // The singles group opens by default. With grouping on it holds most of the
  // page's payments, and shipping it collapsed would mean the common case is a
  // page with one closed card on it.
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(
    new Set(["orphans"]),
  );

  function toggleGroup(key: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleForceRepoll(p: Payout) {
    setRepolling((prev) => new Set(prev).add(p.id));
    try {
      const result = await forceRepollPayout(p.id);
      // Invalidate BOTH cache shapes so the row re-renders with whatever
      // upstream reported on the inline CheckStatus round-trip.
      void qc.invalidateQueries({ queryKey: ["payouts", merchantId] });
      void qc.invalidateQueries({ queryKey: ["merchant-payouts", merchantId] });
      addToast({
        title: result.repaired
          ? `Payout pay_${p.id} repolled`
          : `Payout pay_${p.id} was already pending`,
        body: result.repaired
          ? `Now ${result.payout.status}.`
          : (result.hint ?? "Reconciler will repoll on its next sweep."),
        variant: "success",
      });
    } catch (err) {
      addToast({
        title: `Could not repoll pay_${p.id}`,
        body: apiErrorMessage(
          err,
          "The backend refused the request — your session may need refreshing, or the row was removed.",
        ),
        variant: "error",
      });
    } finally {
      setRepolling((prev) => {
        const next = new Set(prev);
        next.delete(p.id);
        return next;
      });
    }
  }

  // Deep link: /payouts?new=1 (the Dashboard "New payout" button navigates
  // here) opens the modal on arrival. The param is consumed immediately so a
  // refresh or back-navigation doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") !== null) {
      setNewOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Deep link from the Dashboard's KPI tiles: /payouts?status=failure&range=today.
  //
  // A number on a dashboard that cannot be opened is a number you have to go
  // and re-derive by hand. These params make each tile land on exactly the
  // rows it counted, which is the only way to check that a count is right —
  // and the only way to act on it.
  //
  // Consumed once, then stripped from the URL, so a later manual filter
  // change is not silently reverted by a stale param on the next render.
  useEffect(() => {
    const status = searchParams.get("status");
    const range = searchParams.get("range");
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    if (status === null && range === null && from === null && to === null) return;

    if (status === "failure" || status === "pending" || status === "success" ||
        status === "needs_approval" || status === "all") {
      setFilter(status as LivePayoutStatus | "all");
    }
    if (range === "today") {
      // Local calendar day, matching the DateRangeFilter's own "Today"
      // preset. The backend's "today" is 00:00 UTC, so these can disagree by
      // up to 5h30m in IST — the tile links here for the ROWS, and the row
      // dates are what the operator is reading.
      const today = new Date();
      const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      setDateFrom(iso);
      setDateTo(iso);
    } else if (range === "all") {
      setDateFrom("");
      setDateTo("");
    } else if (from !== null || to !== null) {
      // Explicit local "YYYY-MM-DD" bounds from the Dashboard's period
      // picker — the exact window its tiles were counted over. Blank on
      // a side means unbounded, as everywhere else on this page.
      setDateFrom(from ?? "");
      setDateTo(to ?? "");
    }

    const next = new URLSearchParams(searchParams);
    next.delete("status");
    next.delete("range");
    next.delete("from");
    next.delete("to");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // The date range is deliberately NOT part of filtersActive, and not reset
  // by "Clear filters": it is its own always-visible control with its own
  // "All time" reset, so folding it into the collapsible panel's state would
  // let a button the operator can't see wipe a range they can.
  const filtersActive =
    search.trim() !== "" || modeFilter !== "all" || minPaise !== null;

  // The server already filtered; the page is exactly what to show.
  const rows = payouts;

  // Foldable groups: group filtered rows by batch_id. Batches with zero
  // matching rows (after the active filter) are hidden so the operator
  // doesn't see empty headers — clear filters to see them again. Keeps
  // /payouts in lockstep with the /approvals foldable view.
  const groups = useMemo(
    () => groupByBatch(rows, sortDir, groupSingles),
    [rows, sortDir, groupSingles],
  );
  // Counted off the FILTERED rows so the label describes what is actually on
  // screen — and so the control disappears when the current filter leaves
  // nothing to collapse, rather than offering a toggle that does nothing.
  const singletonCount = useMemo(() => countSingletonBatches(rows), [rows]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console</div>
          <h1>Payouts</h1>
          <div className="sub">
            {isLoading ? (
              "Loading…"
            ) : (
              <>
                {total.toLocaleString("en-IN")} payout{total === 1 ? "" : "s"}
                {/* Server totals over EVERY matching row — not a sum of the
                    page on screen. Pure BigInt (CLAUDE.md rule 6). */}
                {Object.entries(data?.amountMinorByCurrency ?? {}).map(([ccy, sum]) => (
                  <span key={ccy}>
                    {" · "}
                    <span style={{ color: "var(--ink-1)" }}>
                      {fmtMoney(sum, ccy)} total
                    </span>
                  </span>
                ))}
                {total > PAGE_SIZE && (
                  <span>
                    {" · "}showing {(pageStart + 1).toLocaleString("en-IN")}–
                    {pageEnd.toLocaleString("en-IN")}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="actions">
          {/* Manual refresh — audit-pass-5 UI batch. Background polling
              still runs on usePayouts's 30s interval, but operators
              investigating a freshly-initiated payout want immediate
              feedback rather than waiting for the next tick. The icon
              spins while isFetching is true so the action is visibly
              acknowledged. The button is disabled during the initial
              load (covered by isLoading) to avoid stacking refetches
              before the first response lands. */}
          <button
            className="btn"
            type="button"
            onClick={() => void refetch()}
            disabled={isLoading}
            aria-label="Refresh payouts"
            title="Refresh payouts"
          >
            <I.Refresh
              size={12}
              style={{
                animation: isFetching
                  ? "spin 800ms linear infinite"
                  : undefined,
              }}
            />{" "}
            Refresh
          </button>
          <button className="btn" type="button" onClick={() => setUploadOpen(true)}>
            <I.Upload size={12} /> CSV upload
          </button>
          <button
            className="btn accent"
            type="button"
            onClick={() => setNewOpen(true)}
          >
            <I.Plus size={12} /> New payout
          </button>
        </div>
      </div>

      <NewPayoutModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        merchantId={merchantId}
      />

      {/* Date range sits in the OPEN toolbar, not inside the collapsible
          Filters panel. On a payout history the date is not a refinement —
          it is the primary question ("what went out on the 9th"), and behind
          a toggle it may as well not exist. Its own row so the status
          segmented control and the presets don't fight for width. */}
      <div className="toolbar" style={{ marginTop: 8 }}>
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={({ from, to }) => {
            setDateFrom(from);
            setDateTo(to);
          }}
        />
      </div>

      <div className="toolbar">
        <Segmented
          value={filter}
          onChange={setFilter}
          options={statusTabs}
        />
        <div className="flex-1" />
        {/* Batch ordering. A <select> rather than a sortable column header:
            the page renders batch CARDS, not a table, so there is no header
            row to click — and the date lives in the card, where a sort
            affordance would compete with the batch_id link. */}
        <label className="row gap-8" style={{ alignItems: "center" }}>
          <span className="dim" style={{ fontSize: 11 }}>
            Sort
          </span>
          <select
            className="input"
            style={{ maxWidth: 150 }}
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value as GroupSort)}
            aria-label="Sort batches by date"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
        {/* Hidden when there is nothing to collapse — a checkbox that would
            change nothing is worse than no checkbox, because the operator has
            to click it to find that out. */}
        {singletonCount > 0 && (
          <label
            className="row gap-8"
            style={{ alignItems: "center", cursor: "pointer" }}
            title="Batches containing a single payout are shown together as one scrollable list instead of one card each."
          >
            <input
              type="checkbox"
              checked={groupSingles}
              onChange={(e) => setGroupSingles(e.target.checked)}
            />
            <span className="dim" style={{ fontSize: 11 }}>
              Group {singletonCount} single-payout batch
              {singletonCount === 1 ? "" : "es"}
            </span>
          </label>
        )}
        <button
          className={"btn ghost sm" + (showFilters || filtersActive ? " active" : "")}
          type="button"
          onClick={() => setShowFilters((s) => !s)}
        >
          <I.Filter size={12} /> Filters{filtersActive ? " ·" : ""}
        </button>
        <button
          className="btn ghost sm"
          type="button"
          disabled={exporting || merchantId == null || total === 0}
          title="Export every payout matching the current filters"
          onClick={async () => {
            if (merchantId == null) return;
            setExporting(true);
            try {
              // Every matching row, page by page — the file must contain
              // what the header counts, not just the page on screen.
              const all = await listAllPayouts(merchantId, listParams);
              downloadCsv(
              "payouts.csv",
              all.map((p) => ({
                id: p.id,
                request_id: p.request_id,
                // Beneficiary identity stamped by the backend list endpoint.
                // Surfacing all three (id, name, account_no, ifsc) so a CSV
                // re-import workflow can match on either the canonical id
                // OR the natural (account_no, ifsc) pair.
                beneficiary_id: p.beneficiary_id ?? "",
                beneficiary_name: p.beneficiary_name ?? "",
                account_no: p.account_no ?? "",
                ifsc: p.ifsc ?? "",
                status: p.status,
                amount_minor: p.amount_minor,
                currency: p.currency,
                txn_mode: p.txn_mode,
                provider_txn_id: p.provider_txn_id,
                utr_no: p.utr_no,
                // Include failure-reason fields so a CSV-triage workflow
                // surfaces WHY a payout failed alongside the row.
                error_code: p.error_code,
                error_message: p.error_message,
                created_at: p.created_at,
              })),
              );
            } catch (err) {
              addToast({
                title: "Export failed",
                body: apiErrorMessage(err, "Could not load the payouts to export."),
                variant: "error",
              });
            } finally {
              setExporting(false);
            }
          }}
        >
          <I.Download size={12} /> {exporting ? "Exporting…" : "Export"}
        </button>
      </div>

      {showFilters && (
        <div
          className="toolbar"
          style={{ gap: 10, flexWrap: "wrap", marginTop: 8 }}
        >
          <input
            className="input"
            style={{ maxWidth: 260 }}
            type="text"
            placeholder="Search request ID, UTR, txn ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="input"
            style={{ maxWidth: 140 }}
            value={modeFilter}
            onChange={(e) => setModeFilter(e.target.value)}
          >
            <option value="all">Any mode</option>
            <option value="1">IMPS</option>
            <option value="2">NEFT</option>
          </select>
          <input
            className="input"
            style={{ maxWidth: 150 }}
            type="text"
            inputMode="decimal"
            placeholder="Min amount ₹"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
          />
          {minAmount.trim() !== "" && minPaise === null && (
            <span style={{ fontSize: 11, color: "var(--warn, #b42318)" }}>
              Invalid amount
            </span>
          )}
          <div className="flex-1" />
          <button
            className="btn ghost sm"
            type="button"
            onClick={() => {
              setSearch("");
              setModeFilter("all");
              setMinAmount("");
            }}
          >
            Clear filters
          </button>
        </div>
      )}

      <div className="card flat">
        {merchantId == null ? (
          // No merchant selected — show an honest "pick a merchant" state
          // instead of the deceptive "No payouts found" empty that the
          // disabled query produces when currentMerchantId is null. Audit
          // pass-3 flagged this as the "No merchants" flash on first
          // /payouts navigation (the user landed before localStorage
          // hydrated, or while the switcher had no selection yet).
          <div className="empty">
            <div className="icon">
              <I.Banks size={16} />
            </div>
            <h3>Pick a merchant</h3>
            <p>
              Payouts are scoped to one merchant at a time. Use the
              merchant switcher at the top of the sidebar to pick one,
              or wait for it to auto-select after sign-in.
            </p>
          </div>
        ) : isLoading ? (
          // Skeleton table — visually distinct from the empty state below.
          // Each cell shows a grey bar at roughly the width of the real
          // content so the layout doesn't reflow when data arrives. Audit
          // pass-3 flagged that the previous "Loading…" centered text was
          // indistinguishable from a true empty state with no rows.
          <table className="tbl">
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Beneficiary</th>
                <th className="mono">Account · IFSC</th>
                <th className="num">Amount</th>
                <th>UTR / Txn</th>
                <th>Created</th>
                <th>Status</th>
                <th>Reason</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td><span className="skel" style={{ width: 140, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 120, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 140, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 70, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 90, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 100, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 56, height: 18, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 110, height: 12, display: "inline-block" }} /></td>
                  {isAdmin && (
                    <td>
                      <span
                        className="skel"
                        style={{ width: 56, height: 18, display: "inline-block" }}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : isError && data == null ? (
          <div className="empty">
            <h3>Couldn't load payouts</h3>
            <p>The request to payouts-backend failed. Try Refresh in a moment.</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Upload size={16} />
            </div>
            <h3>No payouts found</h3>
            <p>
              {filter === "all"
                ? "No payouts have been initiated for this merchant yet."
                : `No payouts with status "${filter}" found.`}
            </p>
            {filter === "all" && (
              <button
                className="btn primary"
                type="button"
                style={{ marginTop: 12 }}
                onClick={() => setNewOpen(true)}
              >
                <I.Plus size={12} /> Initiate first payout
              </button>
            )}
          </div>
        ) : (
          // Grouped, foldable render. Each batch is one card with a
          // header (batch_id link, merchant, row count, total amount,
          // per-status distribution chip). Default collapsed; click the
          // chevron / identity area to expand the inner table.
          // Single payouts — rows with no batch, plus (when the toggle is
          // on) every one-row batch — land in one group at the bottom,
          // rendered as a scrollable table because it can hold every
          // one-off payment on the page.
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {groups.map((g) => {
              const isExpanded = expandedGroups.has(g.key);
              return (
                <div key={g.key} className="card flat" style={{ padding: 0 }}>
                  <PayoutsGroupHeader
                    group={g}
                    isExpanded={isExpanded}
                    onToggle={() => toggleGroup(g.key)}
                    paged={total > PAGE_SIZE}
                  />
                  {isExpanded && (
                    // The singles group can hold every one-row batch on the
                    // page — dozens or hundreds — so its body scrolls in
                    // place rather than pushing the rest of the page out of
                    // reach. A real CSV batch keeps its natural height: it is
                    // one unit of work an operator reads top to bottom.
                    <div
                      className={g.batchId == null ? "singles-scroll" : undefined}
                    >
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Request ID</th>
                          <th>Beneficiary</th>
                          <th className="mono">Account · IFSC</th>
                          <th className="num">Amount</th>
                          <th>UTR / Txn</th>
                          <th>Created</th>
                          <th>Status</th>
                          <th>Reason</th>
                          {/* Admin-only column: force-repoll a terminal
                              row whose status disagrees with IDFC's
                              reality. Hidden from non-admins so the
                              column doesn't render blank for them. */}
                          {isAdmin && <th />}
                        </tr>
                      </thead>
                      <tbody>
                        {g.rows.map((p) => {
                          // Repoll is only meaningful when the row is
                          // terminal AND upstream has a transaction id
                          // to look up.
                          const canRepoll =
                            isAdmin &&
                            (p.status === "failure" ||
                              p.status === "success") &&
                            p.provider_txn_id != null;
                          const isRepolling = repolling.has(p.id);
                          return (
                            <tr
                              key={p.id}
                              // Admin-only drill-down — the destination
                              // page renders provider payload + audit
                              // trail that merchants can't see.
                              role={isAdmin ? "button" : undefined}
                              tabIndex={isAdmin ? 0 : undefined}
                              onClick={
                                isAdmin
                                  ? () => navigate(`/payouts/${p.id}`)
                                  : undefined
                              }
                              onKeyDown={
                                isAdmin
                                  ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        navigate(`/payouts/${p.id}`);
                                      }
                                    }
                                  : undefined
                              }
                              style={
                                isAdmin ? { cursor: "pointer" } : undefined
                              }
                              title={isAdmin ? `Open pay_${p.id}` : undefined}
                            >
                              <td className="mono">{p.request_id}</td>
                              <td>
                                {p.beneficiary_name ?? (
                                  <span className="dim">—</span>
                                )}
                              </td>
                              <td
                                className="mono"
                                style={{ fontSize: 11 }}
                                title={
                                  p.account_no && p.ifsc
                                    ? `${p.account_no} · ${p.ifsc}`
                                    : undefined
                                }
                              >
                                {p.account_no && p.ifsc ? (
                                  <>
                                    {p.account_no}
                                    <span className="dim"> · </span>
                                    {p.ifsc}
                                  </>
                                ) : (
                                  <span className="dim">—</span>
                                )}
                              </td>
                              <td className="num">
                                {fmtCcyMinor(p.amount_minor, p.currency)}
                              </td>
                              <td className="mono dim" style={{ fontSize: 11 }}>
                                {p.utr_no ?? p.provider_txn_id ?? "—"}
                              </td>
                              <td className="dim">
                                {/* Full date incl. year: this list is
                                    date-filterable to any window, so a row
                                    can be read months out of context and
                                    "9 Jun" alone doesn't say which year. */}
                                <time dateTime={p.created_at} title={p.created_at}>
                                  {formatDateTimeFull(p.created_at)}
                                </time>
                                {/* Stale-pending age badge: when a row
                                    has been in 'pending' for >24h the
                                    reconciler has been polling without
                                    upstream resolution. Surface the age
                                    inline so the operator can spot
                                    stuck rows in the table. */}
                                {p.status === "pending" &&
                                  isStalePending(p.created_at) && (
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        fontSize: 10,
                                        padding: "1px 6px",
                                        borderRadius: 3,
                                        background:
                                          "var(--warn-soft, #fef3f2)",
                                        color: "var(--warn, #b42318)",
                                        fontWeight: 500,
                                      }}
                                      title="Pending >24h — escalate to support"
                                    >
                                      stale {ageInHours(p.created_at)}h
                                    </span>
                                  )}
                              </td>
                              <td>
                                <StatusPill status={p.status} />
                              </td>
                              <td>
                                <FailureReason payout={p} />
                              </td>
                              {isAdmin && (
                                // Stop propagation so clicking the
                                // Repoll button doesn't ALSO navigate
                                // into the drill-down.
                                <td onClick={(e) => e.stopPropagation()}>
                                  {canRepoll && (
                                    <button
                                      className="btn ghost sm"
                                      type="button"
                                      onClick={() => handleForceRepoll(p)}
                                      disabled={isRepolling}
                                      title="Reset this row to pending and ask IDFC for the current status. Audited as payout.admin_repair."
                                    >
                                      <I.Refresh
                                        size={11}
                                        style={{
                                          animation: isRepolling
                                            ? "spin 800ms linear infinite"
                                            : undefined,
                                        }}
                                      />{" "}
                                      {isRepolling ? "Repolling…" : "Repoll"}
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              );
            })}
            {total > PAGE_SIZE && (
              <div className="row gap-8" style={{ alignItems: "center", justifyContent: "flex-end", padding: "4px 0" }}>
                <span className="dim" style={{ fontSize: 12 }}>
                  {(pageStart + 1).toLocaleString("en-IN")}–{pageEnd.toLocaleString("en-IN")} of{" "}
                  {total.toLocaleString("en-IN")} · page {page + 1} of {pageCount}
                </span>
                <button
                  className="btn ghost sm"
                  type="button"
                  disabled={page === 0 || isFetching}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  <I.ChevronLeft size={11} /> Previous
                </button>
                <button
                  className="btn ghost sm"
                  type="button"
                  disabled={page + 1 >= pageCount || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next <I.ChevronRight size={11} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Lazy chunk only fetches on first open. Suspense fallback renders
          nothing during the brief network fetch — the modal's own open/close
          animation masks the wait. */}
      {uploadOpen && (
        <Suspense fallback={null}>
          <UploadBatchModal
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
}

// ─── Foldable group header for /payouts ──────────────────────────────────────

// PayoutsGroupHeader is /payouts's batch header. Different from
// Approvals's BatchGroupHeader: no bulk Approve/Reject buttons (those
// are queue-only affordances), and it carries a status-distribution
// chip ("180 success · 5 failure · 15 pending") so the operator sees
// the batch's health without expanding it.
//
// The whole left half is one keyboard-activatable button (chevron +
// identity), mirroring Approvals. The batch_id link's onClick stops
// propagation so it opens /payouts/batches/<id> instead of toggling collapse.
function PayoutsGroupHeader({
  group,
  isExpanded,
  onToggle,
  paged,
}: {
  group: PayoutGroup;
  isExpanded: boolean;
  onToggle: () => void;
  /** The list spans several pages, so this group may hold only part of
   *  its rows — say so on the total rather than pass it off as the whole. */
  paged: boolean;
}) {
  const isOrphan = group.batchId == null;
  const currency = group.rows[0]?.currency ?? "INR";
  const merchantId = group.rows[0]?.merchant_id;
  const counts = statusCounts(group.rows);
  // borderBottom only when expanded — collapsed-with-border reads as a
  // render bug. Mirrors the Approvals header treatment.
  const headerBorder = isExpanded
    ? "1px solid var(--line, rgba(0,0,0,0.06))"
    : "none";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        borderBottom: headerBorder,
        gap: 16,
      }}
    >
      {/* PDF round 3 N3: the previous markup wrapped the entire header in
          a <button> with a <Link> nested inside it — invalid HTML (button
          inside button-ish interactive) and the toggle/navigate behavior
          flipped depending on which descendant absorbed the click. Now
          the toggle is a div with role="button" so the inner Link is a
          well-formed first-class interactive child; stopPropagation on
          the Link keeps the toggle from firing when the link is clicked. */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={isExpanded}
        aria-label={
          isExpanded
            ? `Collapse ${isOrphan ? "single payouts" : `batch_${group.batchId}`}`
            : `Expand ${isOrphan ? "single payouts" : `batch_${group.batchId}`}`
        }
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flex: 1,
          minWidth: 0,
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
          color: "inherit",
          font: "inherit",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 16,
            height: 16,
            color: "var(--ink-2, currentColor)",
          }}
        >
          {isExpanded ? (
            <I.ChevronDown size={14} />
          ) : (
            <I.ChevronRight size={14} />
          )}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {isOrphan ? (
              // Names what is in it, not where it came from. With singleton
              // folding on, this group holds one-row batches as well as rows
              // that never had a batch — and to an operator both are simply
              // "one payment", which is the only distinction worth drawing on
              // a header.
              <>Single payouts</>
            ) : (
              <>
                Batch{" "}
                <Link
                  to={`/payouts/batches/${group.batchId}`}
                  className="mono"
                  title="Open batch detail"
                  onClick={(e) => e.stopPropagation()}
                >
                  batch_{group.batchId}
                </Link>
                {merchantId != null && (
                  <>
                    {" "}
                    · <span className="mono dim">mer_{merchantId}</span>
                  </>
                )}
              </>
            )}
          </div>
          <div
            className="dim"
            style={{
              fontSize: 12,
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {/* Date FIRST in the meta line, and not dimmed like its
                siblings: "which day did this batch go out" is the question
                the header exists to answer, and it was the one thing the
                card could not tell you. The <time> element carries the full
                ISO instant so the exact timestamp is available on hover and
                to assistive tech, while the visible text stays short. */}
            <time
              dateTime={group.firstCreatedAt}
              title={
                group.firstCreatedAt === group.lastCreatedAt
                  ? group.firstCreatedAt
                  : `${group.firstCreatedAt} → ${group.lastCreatedAt}`
              }
              style={{ color: "var(--ink-1)", fontWeight: 500 }}
            >
              {formatDaySpan(group.firstCreatedAt, group.lastCreatedAt)}
            </time>
            <span>
              · {group.rows.length} row{group.rows.length === 1 ? "" : "s"}
            </span>
            {counts.needsApproval > 0 && (
              <span title="needs_approval">
                · {counts.needsApproval} awaiting approval
              </span>
            )}
            {counts.pending > 0 && (
              <span title="pending">· {counts.pending} pending</span>
            )}
            {counts.success > 0 && (
              <span title="success" style={{ color: "var(--ok, #18794e)" }}>
                · {counts.success} success
              </span>
            )}
            {counts.failure > 0 && (
              <span title="failure" style={{ color: "var(--warn, #b42318)" }}>
                · {counts.failure} failure
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Total amount anchor — mirrors /approvals so operators get the
          same visual rhythm whichever page they land on. Tabular-nums
          for digit alignment across stacked groups. */}
      <div style={{ textAlign: "right" }}>
        <div
          className="eyebrow"
          style={{ fontSize: 10, letterSpacing: "0.06em" }}
        >
          {isOrphan ? "Total" : "Batch total"}
          {paged ? " · this page" : ""}
        </div>
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            lineHeight: 1.15,
          }}
        >
          {fmtCcyMinor(group.totalMinor, currency)}
        </div>
      </div>
    </div>
  );
}
